import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UserPublic } from '@mondy/shared';
import { api } from '@/api';
import { ApiError } from '@mondy/shared/api-client';
import { cn } from '@/lib/cn';
import { AdminNav } from '@/components/admin/AdminNav';
import { Spinner } from '@/components/Spinner';

interface UsersResponse {
  users: UserPublic[];
}

export function AdminUsersPage() {
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['admin', 'users', q],
    queryFn: () =>
      api.get<UsersResponse>(
        `/api/admin/users${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ''}`,
      ),
  });

  const toggleStatus = useMutation({
    mutationFn: async (args: { id: string; next: 'active' | 'disabled'; reason?: string }) => {
      return api.put<{ user: UserPublic }>(`/api/admin/users/${args.id}/status`, {
        status: args.next,
        reason: args.reason,
      });
    },
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Update failed');
    },
  });

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-5 p-4 md:p-8">
      <AdminNav />
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold">Admin · Users</h1>
        <p className="text-sm text-[color:var(--color-fg-muted)]">
          Disabled accounts have their existing sessions invalidated immediately.
        </p>
      </header>

      <input
        type="search"
        placeholder="Search email or name"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className="h-11 w-full rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-4 text-base outline-none focus:border-[color:var(--color-accent)]"
      />

      {error ? (
        <p className="text-sm text-[color:var(--color-danger)]">{error}</p>
      ) : null}

      {query.isLoading ? (
        <Spinner fullScreen label="Loading users" />
      ) : query.isError ? (
        <p className="py-8 text-center text-sm text-[color:var(--color-danger)]">
          Couldn't load users.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {query.data!.users.map((u) => (
            <li
              key={u.id}
              className="flex flex-col gap-3 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-3 md:flex-row md:items-center"
            >
              <div className="flex min-w-0 flex-1 flex-col">
                <div className="flex items-center gap-2">
                  <span className="truncate font-medium">{u.displayName}</span>
                  <span
                    className={cn(
                      'shrink-0 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wide',
                      u.role === 'admin'
                        ? 'bg-[color:var(--color-accent)]/20 text-[color:var(--color-accent)]'
                        : 'bg-[color:var(--color-bg)] text-[color:var(--color-fg-muted)]',
                    )}
                  >
                    {u.role}
                  </span>
                  {u.status === 'disabled' ? (
                    <span className="shrink-0 rounded-full bg-[color:var(--color-danger)]/20 px-2 py-0.5 text-[10px] uppercase tracking-wide text-[color:var(--color-danger)]">
                      disabled
                    </span>
                  ) : null}
                </div>
                <span className="truncate text-sm text-[color:var(--color-fg-muted)]">
                  {u.email}
                </span>
              </div>
              <button
                type="button"
                disabled={toggleStatus.isPending}
                onClick={() =>
                  toggleStatus.mutate({
                    id: u.id,
                    next: u.status === 'active' ? 'disabled' : 'active',
                  })
                }
                className={cn(
                  'h-10 shrink-0 rounded-lg px-4 text-sm font-medium transition',
                  u.status === 'active'
                    ? 'border border-[color:var(--color-danger)]/60 text-[color:var(--color-danger)] hover:bg-[color:var(--color-danger)]/10'
                    : 'bg-[color:var(--color-accent)] text-[color:var(--color-accent-fg)]',
                  'disabled:pointer-events-none disabled:opacity-60',
                )}
              >
                {u.status === 'active' ? 'Disable' : 'Reactivate'}
              </button>
            </li>
          ))}
          {query.data!.users.length === 0 ? (
            <li className="py-8 text-center text-sm text-[color:var(--color-fg-muted)]">
              No users match.
            </li>
          ) : null}
        </ul>
      )}
    </div>
  );
}
